import { useCallback, useMemo, useState } from "react";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useGetProviderTypeAttendeesQuery } from "@/store/api/schedule/booking";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectAppointmentTypeGroup,
  selectOptionalAttendees,
  selectPatientId,
  useBookingDispatch,
} from "@/store/reducers/booking";
import AppointmentAvatarCard from "@/ui/components/schedule/booking/AppointmentAvatarCard";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";
import { getOptionalAttendees } from "@/utils/booking.util";

const AppointmentOptionalAttendeesStep = ({ step }: BaseBookingStepProps) => {
  const baseTestID = BookingStepBaseTestIDMap[step];

  const initialOptionalAttendees = useAppSelector(selectOptionalAttendees);
  const patientExternalId = useAppSelector(selectPatientId);
  const typeGroup = useAppSelector(selectAppointmentTypeGroup);
  const { externalId: appUserId } = useAppUserSelector();
  const { setOptionalAttendees } = useBookingDispatch();
  const initialAttendeeIds =
    initialOptionalAttendees?.map((attendee) => attendee.externalId) ?? [];

  const { trackEvent } = useBookingAnalytics();
  const [selectedAttendeeIds, setSelectedAttendeeIds] =
    useState<string[]>(initialAttendeeIds);

  const { data } = useGetProviderTypeAttendeesQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    patientExternalId: patientExternalId!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- grandfathered in, should probably use a { skip: isEmpty(...) || isEmpty(...)
    typeGroup: typeGroup!,
  });

  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });

  const handleForwardNavigation = () => {
    const selectedAttendees = data?.filter((attendee) =>
      selectedAttendeeIds.includes(attendee.externalId),
    );

    if (selectedAttendees) {
      setOptionalAttendees(selectedAttendees);
    }

    onForwardNavigation();
  };

  const attendees = useMemo(() => {
    return getOptionalAttendees({
      attendees: data,
    });
  }, [data]);
  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );

  return (
    <>
      <FlatList
        data={attendees}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const { subtitle, userId, userName, userType } = item;
          const isAppUser = userId === appUserId;
          const isSelected = selectedAttendeeIds.includes(userId);

          const handleOnSelect = () => {
            trackEvent({
              event: BookingAnalyticsEvent.AttendeeSelection,
              source: step,
            });
            setSelectedAttendeeIds((prev) => {
              if (prev.includes(userId)) {
                // eslint-disable-next-line sonarjs/no-nested-functions -- currently reported in SonarQube as unresolved
                return prev.filter((id) => id !== userId);
              }

              return [...prev, userId];
            });
          };

          return (
            <AppointmentAvatarCard
              isAppUser={isAppUser}
              isSelected={isSelected}
              onSelect={handleOnSelect}
              subtitle={subtitle}
              userId={userId}
              userName={userName}
              userType={userType}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentOptionalAttendeesStep;
